import mapboxgl from 'mapbox-gl'
import { useContext, useEffect, useRef } from 'react'
import { MapContext } from '@/src/components/elements/Map'

const icons = {
  default: "bg-[url('/img/markers/marker.svg')]",
  teacher: "bg-[url('/img/markers/teacher_marker.svg')]",
  lesson: "bg-[url('/img/markers/lesson_marker.svg')]",
  pickup: "bg-[url('/img/markers/lesson_marker.svg')]",
  'flch-pickup': "bg-[url('/img/markers/flch_lesson_marker.svg')]",
  'pgs-pickup': "bg-[url('/img/markers/pgs_marker.svg')]",
  'parking-lot': "bg-[url('/img/markers/lesson_marker.svg')]",
  course: "bg-[url('/img/markers/course_marker.svg')]",
  'exam-office': "bg-[url('/img/markers/exam_marker.svg')]",
  wab: "bg-[url('/img/markers/wab_marker.svg')]",
}

export function getIconBasedOnLocationType(
  locationType?: string
): keyof typeof icons {
  if (locationType && Object.keys(icons).includes(locationType)) {
    return locationType as keyof typeof icons
  }
  return 'default'
}

export default function Marker({
  position,
  children,
  icon = 'default',
  size = 'sm',
  focused = false,
  zIndex,
  number,
}: {
  position: { lat: number; lng: number }
  children?: string
  icon?: keyof typeof icons
  size?: 'sm' | 'md' | 'lg'
  focused?: boolean
  zIndex?: number
  number?: number
}) {
  const context = useContext(MapContext)

  const marker = useRef<mapboxgl.Marker | null>(null)
  const markerEl = useRef<Element | null>(null)

  useEffect(() => {
    if (!context.map) {
      return
    }
    if (!context.map.current) {
      return
    }
    if (marker.current) {
      marker.current.remove()
    }
    let popup = undefined

    let markerHeight = 0
    let markerWidth = 0
    switch (size) {
      case 'sm':
        markerWidth = 24
        markerHeight = 30
        break
      case 'md':
        markerWidth = 32
        markerHeight = 40
        break
      case 'lg':
        markerWidth = 40
        markerHeight = 50
        break
    }

    if (children) {
      popup = new mapboxgl.Popup({
        offset: {
          bottom: [0, -markerHeight],
          top: [0, 0],
          left: [markerWidth / 2, -(markerHeight / 2)],
          right: [-markerWidth / 2, -(markerHeight / 2)],
        },
      }).setHTML(children)
    }

    const markerWrapper = document.createElement('div')
    const markerSvg = document.createElement('svg')
    markerSvg.className = `${icons[icon]} marker bg-center bg-no-repeat drop-shadow-sm origin-bottom transition-[scale] duration-200`
    switch (size) {
      case 'sm':
        markerWrapper.style.width = '24px'
        markerWrapper.style.height = '30px'
        markerSvg.style.width = '24px'
        markerSvg.style.height = '30px'
        break
      case 'md':
        markerWrapper.style.width = '32px'
        markerWrapper.style.height = '40px'
        markerSvg.style.width = '32px'
        markerSvg.style.height = '40px'
        break
      case 'lg':
        markerWrapper.style.width = '40px'
        markerWrapper.style.height = '50px'
        markerSvg.style.width = '40px'
        markerSvg.style.height = '50px'
        break
    }
    if (zIndex) {
      markerWrapper.style.zIndex = zIndex.toString()
    }

    markerWrapper.appendChild(markerSvg)

    if (number !== undefined) {
      const numberEl = document.createElement('span')
      numberEl.textContent = String(number)
      switch (size) {
        case 'sm':
          numberEl.style.cssText =
            'position:absolute;top:3px;left:3px;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:white;pointer-events:none;'
          break
        case 'md':
        case 'lg':
          numberEl.style.cssText =
            'position:absolute;top:8px;left:7px;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:white;pointer-events:none;'
          break
      }
      markerSvg.appendChild(numberEl)
    }

    markerEl.current = markerSvg

    marker.current = new mapboxgl.Marker({
      anchor: 'bottom',
      element: markerWrapper,
    })
      .setLngLat([position.lng, position.lat])
      .setPopup(popup)
      .addTo(context.map.current)

    if (popup) {
      const el = marker.current.getElement()
      popup.on('open', () => {
        el.style.zIndex = '49'
      })
      popup.on('close', () => {
        el.style.zIndex = zIndex?.toString() ?? ''
      })
    }

    return () => {
      if (!marker.current) return
      marker.current.remove()
    }
  }, [
    children,
    position.lat,
    position.lng,
    context.map,
    icon,
    size,
    zIndex,
    number,
  ])

  useEffect(() => {
    if (!markerEl.current) return
    markerEl.current.classList.toggle('scale-110', focused)
    if (!marker.current) return
    const element = marker.current.getElement()
    element.style.zIndex = focused ? '48' : (zIndex?.toString() ?? '')
  }, [focused, zIndex])

  return null
}
